/**
 * Componente para exibir mensagem de contato compartilhado
 * 
 * Renderiza informações do contato compartilhado via WhatsApp
 * Similar ao visual do WhatsApp com avatar, nome e telefone
 * Permite iniciar conversa com o contato se ele tiver WhatsApp
 */

import { useState } from 'react';
import { MessageSquare, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { formatPhoneForDisplay } from '@/lib/phone-utils';

interface ContactMessageBubbleProps {
  /** Nome do contato */
  displayName: string;
  /** Telefone do contato (extraído do vCard) */
  phone?: string;
  /** Descrição/cargo do contato (opcional) */
  businessDescription?: string;
  /** vCard original para parsing adicional */
  vcard?: string;
  /** Direção da mensagem */
  direction: 'inbound' | 'outbound';
  /** Callback para iniciar conversa com o contato (quando disponível, substitui link wa.me) */
  onStartConversation?: (phone: string, displayName: string) => Promise<void>;
}

/**
 * Extrai telefone de uma string vCard
 * Suporta formatos: TEL;TYPE=CELL:+55..., TEL:+55..., waid:...
 */
const extractPhoneFromVcard = (vcard?: string): string | undefined => {
  if (!vcard) return undefined;
  
  // Tenta extrair do formato TEL
  const telMatch = vcard.match(/TEL[^:]*:([+\d\s-()]+)/i);
  if (telMatch) return telMatch[1].trim();
  
  // Tenta extrair do formato waid
  const waidMatch = vcard.match(/waid=(\d+)/i);
  if (waidMatch) return waidMatch[1];
  
  return undefined;
};

// Usa função centralizada de phone-utils.ts para formatação

/**
 * Retorna as iniciais do nome para o avatar
 */
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export function ContactMessageBubble({
  displayName,
  phone,
  businessDescription,
  vcard,
  direction,
  onStartConversation,
}: ContactMessageBubbleProps) {
  const [isLoading, setIsLoading] = useState(false);
  const isOutbound = direction === 'outbound';
  
  // Tenta extrair telefone do vCard se não foi passado diretamente
  const contactPhone = phone || extractPhoneFromVcard(vcard);
  const formattedPhone = contactPhone ? formatPhoneForDisplay(contactPhone) : undefined;

  // Handler para iniciar conversa com o contato
  const handleSendMessage = async () => {
    if (!contactPhone) return;
    
    const digits = contactPhone.replace(/\D/g, '');
    
    // Se callback disponível, usa o sistema interno
    if (onStartConversation) {
      setIsLoading(true);
      try {
        await onStartConversation(digits, displayName);
      } finally {
        setIsLoading(false);
      }
    } else {
      // Fallback: abre WhatsApp Web (quando fora do contexto de chat)
      window.open(`https://wa.me/${digits}`, '_blank');
    }
  };

  return (
    <div 
      className="flex flex-col rounded-lg overflow-hidden max-w-[168px]"
      style={{ 
        backgroundColor: '#FFFFFF',
        border: '1px solid #e9edef'
      }}
    >
      {/* Área do contato */}
      <div className="p-2 flex items-center gap-2">
        {/* Avatar com cor fixa WhatsApp */}
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarFallback 
            className="text-xs font-medium"
            style={{ 
              backgroundColor: '#e9edef',
              color: '#667781'
            }}
          >
            {getInitials(displayName)}
          </AvatarFallback>
        </Avatar>

        {/* Informações - cores fixas WhatsApp */}
        <div className="flex-1 min-w-0">
          {/* Nome */}
          <p 
            className="text-xs font-medium truncate"
            style={{ color: '#111B21' }}
          >
            {displayName}
          </p>

          {/* Telefone */}
          {formattedPhone && (
            <p 
              className="text-[10px] truncate"
              style={{ color: '#667781' }}
            >
              {formattedPhone}
            </p>
          )}

          {/* Descrição/Cargo */}
          {businessDescription && (
            <p 
              className="text-[9px] truncate"
              style={{ color: '#8696a0' }}
            >
              {businessDescription}
            </p>
          )}
        </div>
      </div>

      {/* Botão de ação - cor verde WhatsApp */}
      {formattedPhone && (
        <div 
          className="px-2 py-1.5"
          style={{ borderTop: '1px solid #e9edef' }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSendMessage}
            disabled={isLoading}
            className="w-full h-6 text-[10px] gap-1 hover:bg-transparent"
            style={{ color: '#00a884' }}
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <MessageSquare className="h-3 w-3" />
            )}
            {isLoading ? 'Verificando...' : 'Enviar mensagem'}
          </Button>
        </div>
      )}
    </div>
  );
}
